import React, { FormEvent, useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { generateOtp, generateOtpPass, resetPassword, verifymodalOtp } from "../services/apis";

interface ResetErrors {
  email?: string;
  otp?: string;
  newPassword?: string;
  confirmPassword?: string;
}

type ResetStep = "email" | "otp" | "password";

interface PasswordResetFlowProps {
  initialEmail: string;
  onCancel: () => void;
  onComplete: (email: string) => void;
}

const PasswordResetFlow: React.FC<PasswordResetFlowProps> = ({
  initialEmail,
  onCancel,
  onComplete,
}) => {
  const [step, setStep] = useState<ResetStep>("email");
  const [email, setEmail] = useState<string>(initialEmail || "");
  const [otp, setOtp] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<ResetErrors>({});
  const [authError, setAuthError] = useState<string>("");
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);

  useEffect(() => {
    setEmail(initialEmail || "");
  }, [initialEmail]);

  const validateEmail = (value: string): string => {
    if (!value.trim()) return "Email is required";
    if (!/^\S+@\S+\.\S+$/.test(value)) return "Please enter a valid email";
    return "";
  };

  const handleSendOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }
    setAuthError("");
    setLoading(true);
    try {
      await generateOtpPass({ email });
      // toast.success("OTP sent to your email!");
      setErrors({});
      setStep("otp");
    } catch (error: any) {
      setAuthError(error.response?.data?.message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!otp.trim()) {
      setErrors({ otp: "OTP is required" });
      return;
    }
    setAuthError("");
    setLoading(true);
    try {
      await verifymodalOtp({ email, otp });
      //toast.success("OTP verified successfully!");
      setErrors({});
      setStep("password");
    } catch (error: any) {
      setAuthError(error.response?.data?.message || "Invalid OTP!");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newPassword) {
      setErrors({ newPassword: "New password is required" });
      return;
    }
    if (!/^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/.test(newPassword)) {
      setErrors({
        newPassword:
          "Password must be 8+ characters including uppercase, number, and special character",
      });
      return;
    }
    if (!confirmPassword) {
      setErrors({ confirmPassword: "Confirm password is required" });
      return;
    }
    if (confirmPassword !== newPassword) {
      setErrors({ confirmPassword: "Passwords do not match" });
      return;
    }
    setAuthError("");
    setLoading(true);
    try {
      await resetPassword({ email, newPassword });
      //toast.success("Password reset successfully!");
      setResetSuccess(true);
      setTimeout(() => {
        onComplete(email);
        onCancel();
      }, 1000);
    } catch (error: any) {
      setAuthError(error.response?.data?.message || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {step === "email" && (
        <form onSubmit={handleSendOtp} className="space-y-4">
          {authError && <p className="text-red-500 text-sm">{authError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) setErrors({});
                if (authError) setAuthError("");
              }}
              placeholder="john@example.com"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.email
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-black"
              }`}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black toggle-selected-text py-2 px-4 rounded-lg font-medium"
          >
            Send OTP
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="dm-auth-switch w-full border border-gray-300 text-black py-2 px-4 rounded-lg hover:bg-gray-50 hover:text-black font-medium"
          >
            Back to Sign In
          </button>
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          {authError && <p className="text-red-500 text-sm">{authError}</p>}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enter OTP sent to {email}
            </label>
            <input
              type="text"
              value={otp}
              onChange={(e) => {
                setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                if (errors.otp) setErrors({});
                if (authError) setAuthError("");
              }}
              placeholder="6-digit OTP"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.otp
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-black"
              }`}
            />
            {errors.otp && (
              <p className="text-red-500 text-xs mt-1">{errors.otp}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black toggle-selected-text py-2 px-4 rounded-lg font-medium"
          >
            Verify OTP
          </button>

          <button
            type="button"
            onClick={() => setStep("email")}
            className="w-full border border-gray-300 text-black py-2 px-4 rounded-lg hover:bg-gray-50 font-medium"
          >
            Edit Email
          </button>
        </form>
      )}

      {step === "password" && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          {authError && <p className="text-red-500 text-sm">{authError}</p>}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword) setErrors({});
                if (authError) setAuthError("");
              }}
              placeholder="••••••••"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.newPassword
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-black"
              }`}
            />
            <span
              className="absolute right-3 top-9 cursor-pointer text-gray-500"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
            {errors.newPassword && (
              <p className="text-red-500 text-xs mt-1">{errors.newPassword}</p>
            )}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) setErrors({});
                if (authError) setAuthError("");
              }}
              placeholder="••••••••"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.confirmPassword
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-black"
              }`}
            />
            <span
              className="absolute right-3 top-9 cursor-pointer text-gray-500"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </span>
            {errors.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || resetSuccess}
            className="w-full bg-black toggle-selected-text py-2 px-4 rounded-lg font-medium"
          >
            {resetSuccess ? "Password updated" : "Reset Password"}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="dm-auth-switch w-full border border-gray-300 text-black py-2 px-4 rounded-lg hover:bg-gray-50 hover:text-black font-medium"
          >
            Back to Sign In
          </button>
        </form>
      )}
    </div>
  );
};

export { PasswordResetFlow };
